import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding, resolveAsset, DEFAULT_BRANDING, mergeBranding, applyBranding, FONT_OPTIONS, RADIUS_OPTIONS, HEIGHT_OPTIONS, BORDER_OPTIONS, WEIGHT_OPTIONS } from "@/lib/branding";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, RotateCcw, Upload, Palette, Type, Square, ImageIcon, Sparkles, Layers, Plus, X, Monitor, Bug } from "lucide-react";

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid={`style-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 shrink-0 rounded-lg grid place-items-center text-white" style={{ background: "var(--brand-primary, #4f46e5)" }}><Icon className="h-4.5 w-4.5" /></div>
        <div>
          <h2 className="font-admin-head text-lg font-bold text-slate-900">{title}</h2>
          {desc && <p className="text-sm text-slate-500">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function ColorRow({ label, testid, value, onChange }) {
  return (
    <div>
      <Label className="text-slate-600 text-xs">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <input type="color" data-testid={`${testid}-swatch`} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 rounded-md border border-slate-200 cursor-pointer bg-white p-0.5" />
        <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 font-mono-x text-xs uppercase" />
      </div>
    </div>
  );
}

function UploadField({ label, testid, value, onChange, help, previewBg = "dark", usage }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current?.click();
  const tileBg = previewBg === "light" ? "bg-white" : "bg-slate-900";
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/branding/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-slate-600 text-xs">{label}</Label>
        {usage && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Used on: {usage}</span>}
      </div>
      <div className="mt-1 flex items-center gap-3">
        <div className={`h-14 w-14 shrink-0 rounded-lg border border-slate-200 ${tileBg} grid place-items-center overflow-hidden`}>
          {value ? <img src={resolveAsset(value)} alt="" className="h-full w-full object-contain" /> : <ImageIcon className={`h-5 w-5 ${previewBg === "light" ? "text-slate-300" : "text-slate-500"}`} />}
        </div>
        <div className="flex items-center gap-2">
          <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
          <Button type="button" variant="outline" size="sm" onClick={pick} disabled={busy} data-testid={`${testid}-upload`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}
          </Button>
          {value && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`} className="text-xs text-slate-400 hover:text-rose-600">Remove</button>}
        </div>
      </div>
      {help && <p className="text-[11px] text-slate-400 mt-1">{help}</p>}
    </div>
  );
}

function PillSelect({ label, testid, options, value, onChange }) {
  return (
    <div>
      <Label className="text-slate-600 text-xs">{label}</Label>
      <div className="mt-1 flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`${testid}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${String(value) === String(o.value) ? "border-transparent text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            style={{ background: String(value) === String(o.value) ? "var(--brand-primary)" : undefined }}
          >{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function FontSelect({ label, testid, value, onChange }) {
  return (
    <div>
      <Label className="text-slate-600 text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {FONT_OPTIONS.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: `'${f}'` }}>{f}</span></SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function SiteStyle() {
  const { user } = useAuth();
  const { branding, setBranding } = useBranding();
  const [form, setForm] = useState(mergeBranding(branding));
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  useEffect(() => { setForm(mergeBranding(branding)); }, [branding]);
  const loadPresets = () => api.get("/settings/branding/presets").then((r) => setPresets(r.data || [])).catch(() => {});
  useEffect(() => { loadPresets(); }, []);
  // Live-preview fonts app-wide while editing (persisted only on Save).
  useEffect(() => { applyBranding(form); }, [form]);
  // "Try-then-save": if the user leaves without saving, restore the last saved branding.
  const savedRef = useRef(branding);
  useEffect(() => { savedRef.current = branding; }, [branding]);
  useEffect(() => () => { applyBranding(mergeBranding(savedRef.current)); }, []);

  if (user?.role !== "super_admin" && user?.role !== "super_duper_admin") {
    return <p className="text-sm text-slate-500" data-testid="style-forbidden">Only Super Admins can manage site style.</p>;
  }

  const top = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const nest = (g, k, v) => setForm((f) => ({ ...f, [g]: { ...f[g], [k]: v } }));

  const save = async (payload) => {
    const body = payload && !payload.nativeEvent ? payload : form;
    setSaving(true);
    try {
      const r = await api.put("/settings/branding", body);
      setBranding(r.data);
      if (payload) setForm(mergeBranding(r.data));
      toast.success("Site style saved — applied across the app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const savePreset = async () => {
    try {
      await api.post("/settings/branding/presets", { name: presetName.trim(), branding: form });
      setPresetName("");
      loadPresets();
      toast.success("Preset saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save preset");
    }
  };
  const applyPreset = (p) => { const m = mergeBranding(p.branding); setForm(m); save(m); };
  const delPreset = async (id) => { try { await api.delete(`/settings/branding/presets/${id}`); loadPresets(); toast.info("Preset deleted"); } catch { /* ignore */ } };

  const resetDefaults = () => { setForm(mergeBranding(DEFAULT_BRANDING)); toast.info("Reset to defaults — click Save to apply"); };

  const radius = (RADIUS_OPTIONS.find((r) => r.value === form.button?.radius) || RADIUS_OPTIONS[1]).css;
  const gradientCss = `linear-gradient(${form.gradient.angle}deg, ${form.gradient.from}, ${form.gradient.to})`;
  const kioskGradientCss = `linear-gradient(${form.kiosk_gradient.angle}deg, ${form.kiosk_gradient.from}, ${form.kiosk_gradient.to})`;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="site-style-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-admin-head text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2"><Palette className="h-6 w-6" style={{ color: "var(--brand-primary)" }} /> Site Style</h1>
          <p className="text-sm text-slate-500">Manage logos, colours, fonts and buttons across the admin portal and kiosk. Applied everywhere on save.</p>
        </div>
        <Button variant="outline" onClick={resetDefaults} data-testid="style-reset"><RotateCcw className="h-4 w-4 mr-1.5" /> Reset</Button>
        <Button onClick={() => save()} disabled={saving} data-testid="style-save" style={{ background: "var(--brand-primary)" }} className="text-white hover:opacity-90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          <Section icon={Bug} title="Issue Reporting" desc="Show the floating 'Report an issue' button for all admin users.">
            <div className="flex items-center gap-3">
              <Switch checked={!!form.bug_reporting?.enabled} onCheckedChange={(v) => nest("bug_reporting", "enabled", v)} data-testid="style-bug-toggle" />
              <span className="text-sm text-slate-600">{form.bug_reporting?.enabled ? "Bug flagging is ON" : "Bug flagging is OFF"}</span>
            </div>
          </Section>

          <Section icon={Layers} title="Theme Presets" desc="Save the current look as a named preset, or apply one in a click.">
            <div className="flex flex-wrap gap-2 items-center" data-testid="preset-list">
              {presets.map((p) => (
                <div key={p.id} className="flex items-center gap-1 rounded-full border border-slate-200 bg-white pl-3 pr-1 py-1">
                  <button onClick={() => applyPreset(p)} data-testid={`preset-apply-${p.id}`} className="text-sm font-medium text-slate-700 hover:text-slate-900">{p.name}</button>
                  <button onClick={() => delPreset(p.id)} data-testid={`preset-delete-${p.id}`} title="Delete preset" className="h-6 w-6 grid place-items-center rounded-full text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {!presets.length && <span className="text-xs text-slate-400">No presets yet — save your current style below.</span>}
            </div>
            <div className="mt-3 flex items-center gap-2 max-w-md">
              <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Preset name (e.g. FORA, Bruntwood)" data-testid="preset-name-input" className="h-9" />
              <Button variant="outline" size="sm" onClick={savePreset} disabled={!presetName.trim()} data-testid="preset-save"><Plus className="h-4 w-4 mr-1.5" /> Save current</Button>
            </div>
          </Section>

          <Section icon={Type} title="Brand Name" desc="Shown as the wordmark when no logo is set, in the login & admin footers, and as the browser tab title.">
            <Input data-testid="style-brand-name" value={form.brand_name || ""} onChange={(e) => top("brand_name", e.target.value)} className="mt-1" placeholder="The Good Box" />
          </Section>

          <Section icon={ImageIcon} title="Logos & Icons" desc="Pick the right logo for dark vs light areas so it always has contrast.">
            <div className="grid sm:grid-cols-2 gap-5">
              <UploadField label="Logo — on dark areas" testid="style-logo" value={form.logo_url} onChange={(v) => top("logo_url", v)} previewBg="dark" usage="Login hero · Kiosk screens" help="Use a white / light logo. Shown on the dark login panel and kiosk splash." />
              <UploadField label="Logo — on light areas" testid="style-logo-light" value={form.logo_light_url} onChange={(v) => top("logo_light_url", v)} previewBg="light" usage="Admin sidebar" help="Use a dark / colour logo. Shown on the white admin sidebar." />
            </div>
            <div className="mt-5 max-w-xs">
              <UploadField label="Favicon" testid="style-favicon" value={form.favicon_url} onChange={(v) => top("favicon_url", v)} previewBg="dark" usage="Browser tab" help="Small square icon for the browser tab." />
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <Label className="text-slate-600 text-xs">Logo size</Label>
                <span data-testid="style-logo-scale-value" className="text-xs font-semibold text-slate-500">{form.logo_scale || 100}%</span>
              </div>
              <input
                type="range" data-testid="style-logo-scale" min="50" max="160" step="5"
                value={form.logo_scale || 100}
                onChange={(e) => top("logo_scale", Number(e.target.value))}
                className="w-full mt-2 accent-[var(--brand-primary,#7C3AED)]"
              />
              <p className="text-[11px] text-slate-400 mt-1">Scales the logo on the login screen, admin sidebar and kiosk. Capped so it always fits its container.</p>
            </div>
          </Section>

          <Section icon={Type} title="Login Hero Text" desc="The headline and subtitle on the login screen (shown in the preview below).">
            <div className="space-y-4">
              <div>
                <Label className="text-slate-600 text-xs">Headline</Label>
                <Input data-testid="style-hero-title" value={form.hero?.title || ""} onChange={(e) => nest("hero", "title", e.target.value)} className="mt-1" placeholder="Run every kiosk from one place." />
                <p className="text-[11px] text-slate-400 mt-1">Tip: press Enter for a line break in the headline.</p>
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Subtitle</Label>
                <Input data-testid="style-hero-subtitle" value={form.hero?.subtitle || ""} onChange={(e) => nest("hero", "subtitle", e.target.value)} className="mt-1" placeholder="Manage products, stock and reporting across your estate." />
              </div>
            </div>
          </Section>

          <Section icon={Palette} title="Brand Colours" desc="Primary drives buttons and accents across the portal.">
            <div className="grid sm:grid-cols-3 gap-4">
              <ColorRow label="Primary" testid="style-color-primary" value={form.colors.primary} onChange={(v) => nest("colors", "primary", v)} />
              <ColorRow label="Accent" testid="style-color-accent" value={form.colors.accent} onChange={(v) => nest("colors", "accent", v)} />
              <ColorRow label="Text (on dark)" testid="style-color-text" value={form.colors.text} onChange={(v) => nest("colors", "text", v)} />
              <ColorRow label="Background (dark)" testid="style-color-bg" value={form.colors.background} onChange={(v) => nest("colors", "background", v)} />
              <ColorRow label="Surface" testid="style-color-surface" value={form.colors.surface} onChange={(v) => nest("colors", "surface", v)} />
            </div>
          </Section>

          <Section icon={Sparkles} title="Hero Gradient" desc="Used on the login / admin hero panel.">
            <div className="grid sm:grid-cols-3 gap-4">
              <ColorRow label="From" testid="style-grad-from" value={form.gradient.from} onChange={(v) => nest("gradient", "from", v)} />
              <ColorRow label="To" testid="style-grad-to" value={form.gradient.to} onChange={(v) => nest("gradient", "to", v)} />
              <div>
                <Label className="text-slate-600 text-xs">Angle ({form.gradient.angle}°)</Label>
                <input type="range" min="0" max="360" value={form.gradient.angle} data-testid="style-grad-angle" onChange={(e) => nest("gradient", "angle", Number(e.target.value))} className="w-full mt-3 accent-indigo-600" />
              </div>
            </div>
            <div className="mt-4 h-16 rounded-xl border border-slate-200" style={{ background: gradientCss }} data-testid="style-grad-preview" />
          </Section>

          <Section icon={Monitor} title="Kiosk Gradient" desc="Full-screen background on the customer kiosk (idle & payment screens).">
            <div className="grid sm:grid-cols-3 gap-4">
              <ColorRow label="From" testid="style-kgrad-from" value={form.kiosk_gradient.from} onChange={(v) => nest("kiosk_gradient", "from", v)} />
              <ColorRow label="To" testid="style-kgrad-to" value={form.kiosk_gradient.to} onChange={(v) => nest("kiosk_gradient", "to", v)} />
              <div>
                <Label className="text-slate-600 text-xs">Angle ({form.kiosk_gradient.angle}°)</Label>
                <input type="range" min="0" max="360" value={form.kiosk_gradient.angle} data-testid="style-kgrad-angle" onChange={(e) => nest("kiosk_gradient", "angle", Number(e.target.value))} className="w-full mt-3 accent-indigo-600" />
              </div>
            </div>
            <div className="mt-4 h-16 rounded-xl border border-slate-200" style={{ background: kioskGradientCss }} data-testid="style-kgrad-preview" />
          </Section>

          <Section icon={Type} title="Typography" desc="Curated Google Fonts and weights. Body applies to the kiosk too.">
            <div className="grid sm:grid-cols-3 gap-4">
              <FontSelect label="Headings" testid="style-font-heading" value={form.fonts.heading} onChange={(v) => nest("fonts", "heading", v)} />
              <FontSelect label="Sub-headings" testid="style-font-sub" value={form.fonts.subheading} onChange={(v) => nest("fonts", "subheading", v)} />
              <FontSelect label="Body" testid="style-font-body" value={form.fonts.body} onChange={(v) => nest("fonts", "body", v)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <PillSelect label="Heading weight" testid="style-font-heading-weight" options={WEIGHT_OPTIONS} value={form.fonts.heading_weight} onChange={(v) => nest("fonts", "heading_weight", Number(v))} />
              <PillSelect label="Body weight" testid="style-font-body-weight" options={WEIGHT_OPTIONS} value={form.fonts.body_weight} onChange={(v) => nest("fonts", "body_weight", Number(v))} />
            </div>
          </Section>

          <Section icon={Square} title="Buttons" desc="Applies to buttons across the admin portal.">
            <div className="grid sm:grid-cols-2 gap-4">
              <PillSelect label="Corner radius" testid="style-btn-radius" options={RADIUS_OPTIONS} value={form.button.radius} onChange={(v) => nest("button", "radius", v)} />
              <PillSelect label="Height" testid="style-btn-height" options={HEIGHT_OPTIONS} value={form.button.height} onChange={(v) => nest("button", "height", v)} />
              <PillSelect label="Border" testid="style-btn-border" options={BORDER_OPTIONS} value={form.button.border_width} onChange={(v) => nest("button", "border_width", v)} />
              <ColorRow label="Border colour" testid="style-btn-border-color" value={form.button.border_color} onChange={(v) => nest("button", "border_color", v)} />
            </div>
          </Section>

          <Section icon={Type} title="Input Fields" desc="Applies to text inputs across the admin portal.">
            <div className="grid sm:grid-cols-2 gap-4">
              <PillSelect label="Corner radius" testid="style-field-radius" options={RADIUS_OPTIONS} value={form.field.radius} onChange={(v) => nest("field", "radius", v)} />
              <PillSelect label="Height" testid="style-field-height" options={HEIGHT_OPTIONS} value={form.field.height} onChange={(v) => nest("field", "height", v)} />
              <PillSelect label="Border" testid="style-field-border" options={BORDER_OPTIONS} value={form.field.border_width} onChange={(v) => nest("field", "border_width", v)} />
              <ColorRow label="Border colour" testid="style-field-border-color" value={form.field.border_color} onChange={(v) => nest("field", "border_color", v)} />
            </div>
          </Section>

          <Section icon={ImageIcon} title="Background Image" desc="Optional image layered behind the login hero.">
            <UploadField label="Login background" testid="style-bg-login" value={form.backgrounds.login} onChange={(v) => nest("backgrounds", "login", v)} help="Subtle photo/texture behind the gradient." />
          </Section>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-6 space-y-4" data-testid="style-preview">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Live preview</p>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <div className="p-6 text-white relative" style={{ background: gradientCss }}>
              {form.backgrounds.login && <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${resolveAsset(form.backgrounds.login)})` }} />}
              <div className="relative">
                {form.logo_url ? <img src={resolveAsset(form.logo_url)} alt="logo" className="w-auto object-contain mb-4" style={{ height: `calc(28px * ${Math.max(0.5, Math.min(1.6, (form.logo_scale || 100) / 100))})`, maxHeight: "56px" }} /> : <div className="text-white font-extrabold text-lg mb-4" style={{ fontFamily: `'${form.fonts.heading}'` }}>{(form.brand_name || "The Good Box").toUpperCase()}</div>}
                <h3 className="text-2xl font-extrabold leading-tight whitespace-pre-line" style={{ fontFamily: `'${form.fonts.heading}'` }}>{form.hero?.title || "Run every kiosk from one place."}</h3>
                <p className="mt-2 text-white/80 text-sm" style={{ fontFamily: `'${form.fonts.body}'` }}>{form.hero?.subtitle || "Manage products, stock and reporting across your estate."}</p>
              </div>
            </div>
            <div className="p-6 bg-white space-y-4">
              <h4 className="font-bold text-slate-900" style={{ fontFamily: `'${form.fonts.subheading}'` }}>Sub-heading example</h4>
              <p className="text-sm text-slate-600" style={{ fontFamily: `'${form.fonts.body}'` }}>Body text renders in your chosen font. This is how paragraphs and labels will read across the app.</p>
              <div className="flex flex-wrap gap-2">
                <button className="px-4 py-2 text-sm font-semibold text-white" style={{ background: form.colors.primary, borderRadius: radius }} data-testid="style-preview-primary-btn">Primary</button>
                <button className="px-4 py-2 text-sm font-semibold text-white" style={{ background: form.colors.accent, borderRadius: radius }}>Accent</button>
                <button className="px-4 py-2 text-sm font-semibold border border-slate-200 text-slate-700" style={{ borderRadius: radius }}>Outline</button>
              </div>
              <div className="flex items-center gap-2">
                {["primary", "accent", "background", "surface"].map((k) => (
                  <span key={k} title={k} className="h-8 w-8 rounded-full border border-slate-200" style={{ background: form.colors[k] }} />
                ))}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400">Changes preview instantly here. Click <b>Save changes</b> to apply across the admin portal and customer kiosk.</p>
        </div>
      </div>
    </div>
  );
}
